import { useCallback, useEffect, useRef, useState } from "react";

type SnapshotItem = [string, string];

interface HookMember {
  loading: boolean;
  list: SnapshotItem[];
  filter: string;
  setFilter: (filter: string) => void;
  search: (applyFilter: boolean) => void;
  reverse: boolean;
  setReverse: (reverse: boolean) => void;
  page: number;
  setPage: (page: number) => void;
  pageLimit: number;
  totalCount: number;
}

const PAGE_SIZE = 8;

const useAdList = (): HookMember => {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [list, setList] = useState<SnapshotItem[]>([]);
  const [filter, setFilter] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");
  const [reverse, setReverse] = useState(false);
  const [pageLimit, setPageLimit] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const requestId = useRef(0);

  const fetchData = useCallback(async (selectedPage: number, selectedFilter: string) => {
    const currentRequestId = ++requestId.current;
    setLoading(true);

    try {
      const searchParams = new URLSearchParams({
        page: String(selectedPage),
        limit: String(PAGE_SIZE),
        filter: selectedFilter,
      });
      const response = await fetch(
        `${process.env.REACT_APP_URL}/adddi-light-registration/imageList?${searchParams.toString()}`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const jsonData = await response.json();
      if (currentRequestId !== requestId.current) return;

      if (jsonData.status === 200 && Array.isArray(jsonData.data)) {
        const count = Number(jsonData.data[0] || 0);
        setTotalCount(count);
        setPageLimit(Math.max(1, Math.ceil(count / PAGE_SIZE)));
        setList(Array.isArray(jsonData.data[1]) ? jsonData.data[1] : []);
        return;
      }

      setTotalCount(0);
      setPageLimit(1);
      setList([]);
    } catch {
      if (currentRequestId !== requestId.current) return;
      setTotalCount(0);
      setPageLimit(1);
      setList([]);
    } finally {
      if (currentRequestId === requestId.current) setLoading(false);
    }
  }, []);

  const search = (applyFilter: boolean) => {
    const nextFilter = applyFilter ? filter.trim() : "";
    if (!applyFilter) setFilter("");

    if (page === 1 && appliedFilter === nextFilter) {
      fetchData(1, nextFilter);
      return;
    }

    setPage(1);
    setAppliedFilter(nextFilter);
  };

  useEffect(() => {
    fetchData(page, appliedFilter);
  }, [appliedFilter, fetchData, page]);

  return {
    loading,
    list,
    filter,
    setFilter,
    search,
    reverse,
    setReverse,
    page,
    setPage,
    pageLimit,
    totalCount,
  };
};

export default useAdList;
